import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Plus, Trash2 } from 'lucide-react';

import { getQueueDetail } from '@/api/analytics';
import { useT } from '@/contexts/I18nContext';
import {
  spliceLine,
  setLinkTimeRange,
  type LongestQueueLine,
  type LongQueueLine,
  type NoVisitorsLine,
} from '../constructor/lineModel';
import { serializeLongestQueue } from '../constructor/treeBlock';
import { LongQueueForm, NoVisitorsForm } from '../constructor/TreeQueueForms';
import { AnnotatableTimeChart } from '../chart/AnnotatableTimeChart';
import type { ChartSeries, ChartSegment, ChartPhoto, Domain } from '../chart/types';

const QUEUE_BUCKET_MS = 60 * 1000;
const QUEUE_PHOTO_COUNT = 40;

/**
 * Interactive editor for the NEW tree-format "Очередь и бар" lines: the
 * "Самая длинная очередь" tree block gets a chip-added, drag-adjustable
 * peak segment (same one-segment-only convention the old `q_longest`
 * chart used in QueueChartBlock.tsx — never empty-drag-created). The two
 * flat lines ("Очередь от N человек" / "Без посетителей у кассы") have no
 * natural chart geometry of their own, so they reuse their V2 forms
 * inline below the chart, same pattern QueueChartBlock uses for
 * `q_novisit`.
 */
export function TreeQueueChartBlock({
  longestLine,
  longQueueLine,
  noVisitorsLine,
  zoneId,
  day,
  value,
  onChange,
  disabled,
}: {
  longestLine: LongestQueueLine | null;
  longQueueLine: LongQueueLine | null;
  noVisitorsLine: NoVisitorsLine | null;
  zoneId: string;
  day: string;
  value: string;
  onChange: (bodyHtml: string) => void;
  disabled?: boolean;
}) {
  const { t } = useT();

  const { data } = useSWR(
    zoneId ? ['rv-interactive-queue-tree', zoneId, day] : null,
    () => getQueueDetail(zoneId, day, day, QUEUE_BUCKET_MS, { photoCount: QUEUE_PHOTO_COUNT }).then((r) => r.data),
    { revalidateOnFocus: false },
  );

  const domainFull = useMemo<Domain | null>(() => {
    const wh = data?.working_hours;
    if (!wh) return null;
    const s = new Date(wh.start).getTime();
    const e = new Date(wh.end).getTime();
    return Number.isFinite(s) && Number.isFinite(e) && e > s ? [s, e] : null;
  }, [data?.working_hours]);

  const queuePoints = useMemo(
    () => (data?.overlay ?? []).map((p) => ({ t: new Date(p.time).getTime(), v: p.queue })),
    [data?.overlay],
  );
  const cashierPoints = useMemo(
    () => (data?.overlay ?? []).map((p) => ({ t: new Date(p.time).getTime(), v: p.cashier_count })),
    [data?.overlay],
  );
  const photos: ChartPhoto[] = useMemo(
    () =>
      (data?.queue_photos ?? [])
        .filter((p) => p.image_url)
        .map((p) => ({
          t: new Date(p.at).getTime(),
          url: p.image_url!,
          caption: p.captured_occupancy != null ? `${p.captured_occupancy} чел.` : undefined,
        })),
    [data?.queue_photos],
  );
  const series: ChartSeries[] = useMemo(
    () => [
      {
        id: 'queue',
        kind: 'bar',
        points: queuePoints,
        axis: 'left',
        color: '#f59e0b',
        label: t('reportValidation.interactive.queueLabel'),
        bucketMs: QUEUE_BUCKET_MS,
      },
      {
        id: 'cashiers',
        kind: 'step',
        points: cashierPoints,
        axis: 'right',
        color: '#6366f1',
        label: t('report.day.openCashiers'),
      },
    ],
    [queuePoints, cashierPoints, t],
  );

  const [seg, setSeg] = useState<ChartSegment | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const pushLongest = (s: ChartSegment | null) => {
    if (!longestLine) return;
    if (!s) return; // removed → leave text as last written; validator can still use the V2 form
    const inRange = queuePoints.filter((p) => p.t >= s.t_start && p.t <= s.t_end);
    const peopleCount = inRange.length ? Math.max(...inRange.map((p) => p.v)) : 0;
    // «Спала за» — от пика до конца выделенного участка, как в отчёте (checkout_control:
    // пик → первый ноль). Раньше бралась вся длина выделения (с начала роста), и правка
    // валидатора удлиняла время в среднем на 8 мин без ошибки модели (Алексей 27.10 #3035).
    const peakT = inRange.length ? inRange.reduce((a, p) => (p.v > a.v ? p : a)).t : s.t_start;
    const drainMinutes = Math.max(1, Math.round((s.t_end - peakT) / 60000));
    const linkUrl = longestLine.fields.linkUrl ? setLinkTimeRange(longestLine.fields.linkUrl, s.t_start, s.t_end) : null;
    onChange(
      spliceLine(
        value,
        longestLine,
        serializeLongestQueue({ ...longestLine.fields, peopleCount, drainMinutes, linkUrl }),
      ),
    );
  };

  const readout = (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
      {seg ? (
        <>
          <span className="font-medium">{t('reportValidation.tf.q_longest')}</span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setSeg(null);
              setSelectedId(null);
            }}
            className="ml-auto rounded-md border border-border p-1 text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="size-3.5" />
          </button>
        </>
      ) : (
        longestLine && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              let peakT = domainFull?.[0] ?? 0;
              let peakV = -1;
              for (const p of queuePoints) {
                if (p.v > peakV) {
                  peakV = p.v;
                  peakT = p.t;
                }
              }
              const next: ChartSegment = {
                id: `longest-${peakT}`,
                t_start: peakT - 10 * 60000,
                t_end: peakT + 10 * 60000,
              };
              setSeg(next);
              setSelectedId(next.id);
              pushLongest(next);
            }}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-0.5 text-muted-foreground hover:border-primary hover:text-primary"
          >
            <Plus className="size-3" /> {t('reportValidation.tf.q_longest')}
          </button>
        )
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      {domainFull ? (
        <AnnotatableTimeChart
          series={series}
          photos={photos}
          domainFull={domainFull}
          height={260}
          annotateCreates="none"
          segments={seg ? [seg] : []}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onSegmentChange={(next) => {
            setSeg(next);
            pushLongest(next);
          }}
          readout={readout}
        />
      ) : (
        <div className="h-56 animate-pulse rounded-xl bg-muted" />
      )}
      {longQueueLine && (
        <div className="rounded-xl border border-border bg-card p-3">
          <LongQueueForm
            initial={longQueueLine.fields}
            onApply={(row) => onChange(spliceLine(value, longQueueLine, [row]))}
            onCancel={() => {}}
          />
        </div>
      )}
      {noVisitorsLine && (
        <div className="rounded-xl border border-border bg-card p-3">
          <NoVisitorsForm
            initial={noVisitorsLine.fields}
            onApply={(row) => onChange(spliceLine(value, noVisitorsLine, [row]))}
            onCancel={() => {}}
            hidePreview
          />
        </div>
      )}
    </div>
  );
}
